// Global mobile counterparts to the space-scoped useMentionsFeed / useUnreads:
// the phone has no space switcher, so these scope by global room visibility
// (joined, non-archived, non-space) instead of the active space.

import { useMemo } from "react";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import {
  isMentionItem,
  type MentionItem,
  type UseMentionsFeedResult,
} from "~/hooks/useMentionsFeed";
import type { UseUnreadsResult } from "~/hooks/useUnreads";
import { sendReadReceipt } from "~/lib/matrix";

function useGlobalVisibleRoomIds(): Set<string> {
  const rooms = useMatrixRooms();
  return useMemo(
    () =>
      new Set(
        rooms.filter((room) => !room.isSpace && room.archived === null).map((room) => room.id),
      ),
    [rooms],
  );
}

export function useMobileMentions(): UseMentionsFeedResult {
  const { mentionsFeed, clearMentions } = useMatrix();
  const visibleRoomIds = useGlobalVisibleRoomIds();

  const items = useMemo(
    () =>
      (mentionsFeed ?? []).filter(
        (item): item is MentionItem => isMentionItem(item) && visibleRoomIds.has(item.roomId),
      ),
    [mentionsFeed, visibleRoomIds],
  );

  return {
    items,
    clearAll: clearMentions ? () => clearMentions(items) : async () => {},
  };
}

export function useMobileUnreads(): UseUnreadsResult {
  const { client, unreadGroups } = useMatrix();
  const visibleRoomIds = useGlobalVisibleRoomIds();

  const groups = useMemo(
    () => unreadGroups.filter((group) => visibleRoomIds.has(group.roomId)),
    [unreadGroups, visibleRoomIds],
  );

  const { totalUnread, totalHighlight } = useMemo(() => {
    let total = 0;
    let highlight = 0;
    for (const group of groups) {
      total += group.unreadCount;
      highlight += group.highlightCount;
    }
    return { totalUnread: total, totalHighlight: highlight };
  }, [groups]);

  const markAllRead = async () => {
    if (!client) return;
    await Promise.all(
      groups.map(async (group) => {
        const last = group.events[group.events.length - 1];
        if (!last) return;
        await sendReadReceipt(client, group.roomId, last.id);
      }),
    );
  };

  return { groups, totalUnread, totalHighlight, markAllRead };
}
